/**
 * ApiKeysPage - Developer API key management page.
 *
 * Allows users with the 'developer' role to view, create, and revoke
 * API keys used for programmatic access to the GBrands platform.
 *
 * Features:
 *	 1. Header — title with descriptive explanation of API keys
 *	 2. Key List — table/cards showing existing keys: name, prefix, created
 *		date, last used, status, expiration, and a revoke button
 *	 3. Generate Button — opens a modal to create a new key with a name
 *		and optional expiration date
 *	 4. Key Reveal — after creation, shows the full key in a copyable field
 *		with a warning that it cannot be viewed again
 *	 5. Revoke — delete button marks a key as inactive
 *
 * Accessible by: developer role only (protected by <ProtectedRoute>).
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiKeyService } from '../../services/apiKeyService';
import { useAuth } from '../../hooks/useAuth';
import type { ApiKey } from '../../services/apiKeyService';
import { Alert, Button } from '../../components/atoms';
import { PageHeader } from '../../components/molecules/PageHeader';
import { DataTable } from '../../components/organisms/DataTable';
import type { DataTableColumn } from '../../components/organisms/DataTable';
import { qk } from '../../lib/queryKeys';
import { PageContainer } from '../../components/templates/PageContainer';

/* ============================================
 * COMPONENT
 * ============================================ */

const ApiKeysPage: React.FC = () => {
	const { hasAnyRole } = useAuth();
	const queryClient = useQueryClient();

	/* --- Generate modal state --- */
	const [generateModalOpen, setGenerateModalOpen] = useState(false);
	/* Freeze the page behind the hand-rolled Generate Key modal */
	useBodyScrollLock(generateModalOpen);
	const [newKeyName, setNewKeyName] = useState('');
	const [newKeyExpiry, setNewKeyExpiry] = useState('');
	const [generating, setGenerating] = useState(false);
	const [generateError, setGenerateError] = useState<string | null>(null);

	/* --- Revealed key state (shown after successful creation) --- */
	const [revealedKey, setRevealedKey] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	/* --- Revoke state --- */
	const [revokingId, setRevokingId] = useState<string | number | null>(null);
	/* Inline error surfaced from a failed revoke mutation (kept separate from
	 * the list query error so a transient revoke failure doesn't blank the list). */
	const [revokeError, setRevokeError] = useState<string | null>(null);

	/* ============================================
	 * DATA FETCHING
	 * ============================================ */

	/**
	 * Fetches all API keys for the current developer user.
	 * Backed by TanStack Query — shared cache + declarative invalidation
	 * replaces the previous manual fetch/refetch flow.
	 */
	const keysQuery = useQuery({
		queryKey: qk.apiKeys.lists(),
		queryFn: async () => {
			try {
				const res = await apiKeyService.getAll();
				// Handle both array and paginated response shapes
				const keysData = Array.isArray(res.data) ? res.data : (res.data as any)?.data ?? [];
				return keysData as ApiKey[];
			} catch (err: any) {
				// Preserve the api client's wrapped error message shape.
				throw new Error(err?.data?.message || 'Failed to load API keys.');
			}
		},
	});

	const keys = useMemo(() => keysQuery.data ?? [], [keysQuery.data]);
	const loading = keysQuery.isLoading;
	const error = revokeError ?? (keysQuery.error ? (keysQuery.error as Error).message : null);

	/* ============================================
	 * GENERATE KEY HANDLERS
	 * ============================================ */

	/** Opens the generate key modal and resets form fields */
	const openGenerateModal = useCallback(() => {
		setGenerateModalOpen(true);
		setNewKeyName('');
		setNewKeyExpiry('');
		setGenerateError(null);
		setRevealedKey(null);
		setCopied(false);
	}, []);

	/** Closes the generate key modal */
	const closeGenerateModal = useCallback(() => {
		setGenerateModalOpen(false);
		setGenerateError(null);
		// If a key was just revealed, refresh the key list
		if (revealedKey) {
			setRevealedKey(null);
			queryClient.invalidateQueries({ queryKey: qk.apiKeys.all });
		}
	}, [revealedKey, queryClient]);

	/**
	 * Create-key mutation. On success it reveals the full plain-text key;
	 * the list is invalidated when the modal closes (closeGenerateModal),
	 * matching the original "refresh on done" behavior.
	 */
	const createMutation = useMutation({
		mutationFn: (vars: { name: string; expiry?: string }) =>
			apiKeyService.create(vars.name, vars.expiry),
	});

	/**
	 * Submits the generate key form via POST /api-keys.
	 * On success, reveals the full plain-text key to the user.
	 */
	const handleGenerateSubmit = useCallback(async (e: React.FormEvent) => {
		e.preventDefault();
		setGenerateError(null);

		// Validate that a name was provided
		if (!newKeyName.trim()) {
			setGenerateError('Please provide a name for the API key.');
			return;
		}

		setGenerating(true);

		try {
			const res = await createMutation.mutateAsync({
				name: newKeyName.trim(),
				expiry: newKeyExpiry || undefined,
			});
			// Show the full plain-text key for the user to copy
			setRevealedKey(res.data.plain_text_key);
			setCopied(false);
		} catch (err: any) {
			setGenerateError(err?.data?.message || 'Failed to generate API key.');
		} finally {
			setGenerating(false);
		}
	}, [newKeyName, newKeyExpiry, createMutation]);

	/**
	 * Copies the revealed key to the clipboard using the Clipboard API.
	 * Falls back to a selection-based copy for older browsers.
	 */
	const handleCopyKey = useCallback(async () => {
		if (!revealedKey) return;

		try {
			await navigator.clipboard.writeText(revealedKey);
			setCopied(true);
			// Reset the "Copied!" feedback after 2 seconds
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// Fallback: select the text in the input and use execCommand
			setCopied(false);
		}
	}, [revealedKey]);

	/* ============================================
	 * REVOKE KEY HANDLER
	 * ============================================ */

	/**
	 * Revokes an API key by calling DELETE /api-keys/:id.
	 * Marks the key as inactive so it can no longer authenticate requests.
	 */
	const revokeMutation = useMutation({
		mutationFn: (id: string | number) => apiKeyService.revoke(id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.apiKeys.all }),
	});

	const handleRevoke = useCallback(async (id: string | number) => {
		// Confirm before revoking to prevent accidental deletions
		if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
			return;
		}

		setRevokingId(id);
		setRevokeError(null);

		try {
			await revokeMutation.mutateAsync(id);
		} catch (err: any) {
			setRevokeError(err?.data?.message || 'Failed to revoke API key.');
		} finally {
			setRevokingId(null);
		}
	}, [revokeMutation]);

	/* ============================================
	 * HELPER: FORMAT DATE
	 * ============================================ */

	/**
	 * Formats an ISO date string to a human-readable local date.
	 * Returns '--' for null/undefined values.
	 */
	const formatDate = (dateStr: string | null): string => {
		if (!dateStr) return '--';
		try {
			return new Date(dateStr).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			});
		} catch {
			return dateStr;
		}
	};

	/* ============================================
	 * TABLE COLUMN DEFINITIONS
	 * Each column specifies a header label and a render function for cell content.
	 * ============================================ */

	const columns: DataTableColumn<ApiKey>[] = useMemo(() => [
		{ key: 'name', header: 'Name', render: (k) => <span className="text-brand-text font-medium">{k.name}</span> },
		{
			key: 'prefix',
			header: 'Key Prefix',
			render: (k) => (
				/* Key prefix shown in monospace for readability */
				<code className="text-12px bg-brand-surface px-6px py-2px rounded font-mono text-brand-text-muted">{k.prefix}...</code>
			),
		},
		{ key: 'created', header: 'Created', render: (k) => <span className="text-brand-text-muted">{formatDate(k.created_at)}</span> },
		{ key: 'last_used', header: 'Last Used', render: (k) => <span className="text-brand-text-muted">{formatDate(k.last_used_at)}</span> },
		{ key: 'expires', header: 'Expires', render: (k) => <span className="text-brand-text-muted">{formatDate(k.expires_at)}</span> },
		{
			key: 'status',
			header: 'Status',
			render: (k) => (
				<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${k.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
					{k.is_active ? 'Active' : 'Inactive'}
				</span>
			),
		},
		{
			key: 'actions',
			header: 'Actions',
			render: (k) => (
				/* Only show revoke button for active keys.
				   Same 44px floor as the card-view revoke further down — this is
				   the table variant, which still renders on touch tablets. */
				k.is_active ? (
					<button onClick={() => handleRevoke(k.id)} disabled={revokingId === k.id} className="api-keys-page__revoke inline-flex items-center min-h-44px desktop:min-h-0 text-red-600 hover:text-red-800 text-12px font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
						{revokingId === k.id ? 'Revoking...' : 'Revoke'}
					</button>
				) : null
			),
		},
	], [handleRevoke, revokingId]);

	/** Renders a single mobile card for an API key */
	const renderMobileCard = useCallback((key: ApiKey) => (
		<div className="bg-white border border-brand-border rounded-lg p-16px">
			{/* Card header: name + status badge */}
			<div className="flex items-center justify-between mb-8px">
				<span className="font-medium text-brand-text">{key.name}</span>
				<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${key.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
					{key.is_active ? 'Active' : 'Inactive'}
				</span>
			</div>
			{/* Key prefix */}
			<p className="text-12px text-brand-text-muted mb-4px">
				Prefix: <code className="bg-brand-surface px-4px py-2px rounded font-mono">{key.prefix}...</code>
			</p>
			{/* Dates */}
			<p className="text-12px text-brand-text-muted">Created: {formatDate(key.created_at)}</p>
			<p className="text-12px text-brand-text-muted">Last used: {formatDate(key.last_used_at)}</p>
			<p className="text-12px text-brand-text-muted">Expires: {formatDate(key.expires_at)}</p>
			{/* Revoke action */}
			{key.is_active && (
				<div className="mt-8px">
					{/* min-h-44px: revoking is destructive and irreversible, so the
					    control needs a full touch target rather than an 18px link. */}
					<button onClick={() => handleRevoke(key.id)} disabled={revokingId === key.id} className="api-keys-page__revoke inline-flex items-center min-h-44px text-red-600 hover:text-red-800 text-12px font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
						{revokingId === key.id ? 'Revoking...' : 'Revoke Key'}
					</button>
				</div>
			)}
		</div>
	), [handleRevoke, revokingId]);

	/* ============================================
	 * RENDER
	 * ============================================ */

	/* Role gate — API key management is for developers / super admins. The
	 * backend's DeveloperOnly middleware already enforces this on every request,
	 * so this is a UX/consistency gate: a non-developer who deep-links or
	 * bookmarks /api-keys gets a clean Forbidden view (matching CMSPage) instead
	 * of a page that immediately fails to load. */
	if (!hasAnyRole(['developer', 'super_admin'])) {
		return (
			<div className="max-w-[640px] mx-auto mt-48px px-24px py-32px bg-white border border-brand-border rounded-md text-center">
				<h1 className="text-20px font-semibold text-brand-text mb-8px">Forbidden</h1>
				<p className="text-14px text-brand-text-muted">API key management is restricted to Developer users.</p>
			</div>
		);
	}

	return (
		<PageContainer block="api-keys-page" dataRef="page-api-keys-page">
			{/* --- Page header (PageHeader molecule) --- */}
			<PageHeader
				title="API Keys"
				subtitle="API keys allow programmatic access to the GBrands platform. Keep your keys secure and never share them in client-side code or public repositories."
				buttonLabel="Generate New Key"
				onButtonClick={openGenerateModal}
			/>

			{/* Error state */}
			{error && !loading && (
				<Alert variant="error" className="mb-16px">{error}</Alert>
			)}

			{/* --- API Keys DataTable (organism) — renders desktop table + mobile cards --- */}
			<DataTable<ApiKey>
				columns={columns}
				data={keys}
				keyExtractor={(k) => String(k.id)}
				renderMobileCard={renderMobileCard}
				isLoading={loading}
				emptyMessage="No API keys found. Generate your first key to start integrating with the platform."
			/>

			{/* === GENERATE KEY MODAL === */}
			{generateModalOpen && (
				<div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-16px p-24px">
						{/* Modal header */}
						<div className="flex items-center justify-between mb-16px">
							<h3 className="text-18px font-bold font-heading text-brand-text">
								{revealedKey ? 'API Key Generated' : 'Generate New API Key'}
							</h3>
							<button type="button" onClick={closeGenerateModal} className="inline-flex items-center justify-center w-44px h-44px -mr-8px shrink-0 rounded-full text-brand-text-subtle hover:text-brand-text hover:bg-brand-surface transition-colors" aria-label="Close modal">
								<svg className="h-20px w-20px" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
								</svg>
							</button>
						</div>

						{/* --- Key reveal view (shown after successful creation) --- */}
						{revealedKey && (
							<div>
								{/* Warning banner: key is only shown once */}
								<Alert variant="warning" className="mb-16px">Copy this key now. You won't be able to see it again.</Alert>

								{/* Full key display with copy button */}
								<div className="flex items-center gap-8px mb-16px">
									<input type="text" readOnly value={revealedKey} className="flex-1 px-12px py-8px border border-brand-border rounded-md text-16px font-mono text-brand-text bg-brand-surface focus:outline-none" />
									<Button variant="primary" size="sm" onClick={handleCopyKey} className="whitespace-nowrap">
										{copied ? 'Copied!' : 'Copy'}
									</Button>
								</div>

								{/* Done button — closes modal and refreshes the key list */}
								<div className="flex justify-end">
									<Button variant="primary" size="sm" onClick={closeGenerateModal}>
										Done
									</Button>
								</div>
							</div>
						)}

						{/* --- Key creation form (shown before key is generated) --- */}
						{!revealedKey && (
							<>
								{/* Error banner */}
								{generateError && (
									<Alert variant="error" className="mb-12px">{generateError}</Alert>
								)}

								<form onSubmit={handleGenerateSubmit}>
									<div className="space-y-12px">
										{/* Key Name field (required) */}
										<div>
											<label htmlFor="key-name" className="block text-14px font-medium text-brand-text mb-4px">Key Name</label>
											<input type="text" id="key-name" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} className="w-full px-12px py-8px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" placeholder="e.g., Production Integration" />
										</div>

										{/* Expiration Date field (optional) */}
										<div>
											<label htmlFor="key-expiry" className="block text-14px font-medium text-brand-text mb-4px">Expiration Date <span className="text-brand-text-subtle font-normal">(optional)</span></label>
											<input type="date" id="key-expiry" value={newKeyExpiry} onChange={(e) => setNewKeyExpiry(e.target.value)} className="w-full px-12px py-8px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary" />
										</div>
									</div>

									{/* Form actions */}
									<div className="mt-16px flex justify-end gap-8px">
										<Button type="button" variant="secondary" size="sm" onClick={closeGenerateModal}>
											Cancel
										</Button>
										<Button type="submit" variant="primary" size="sm" disabled={generating}>
											{generating ? 'Generating...' : 'Generate Key'}
										</Button>
									</div>
								</form>
							</>
						)}
					</div>
				</div>
			)}
		</PageContainer>
	);
};

export default ApiKeysPage;
